/**
 * CandidateTable Component
 *
 * Shared table display for candidates using StandardTable components.
 * Mirrors LegislatorTable pattern with configurable columns.
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Star,
  Trash2,
  Eye,
  Bookmark,
  Mail,
  Phone,
  Users,
  Globe,
  Share2,
  DollarSign,
} from 'lucide-react';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../StandardTable';
import Chip from '../Chip';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';
import { statusVariant } from '../../constants/candidates';
import { getPartyTextColor, getPartyChipVariant, formatPartyDisplay } from '../../utils/partyUtils';
import { formatPhoneNumber, formatPersonName } from '../../utils/nameUtils';
import { WorkspaceBadgeList } from '../WorkspaceBadge';
import { BaseCandidate, CandidateRenderContext, SortState, CandidateSortField, SocialMediaLink } from './types';
import { toTrackedCandidateIdSet } from './candidateTrackedIds';
import { CandidateCustomFieldColumn, getCustomFieldDisplayValues } from './candidateCustomFieldUtils';
// Was a local copy of this name, shadowing the shared one with a different
// colour language. Support/Oppose keep their green/red; Monitor moves from amber
// to the brand tint, matching every other surface that shows a position.
import { getPositionColorClasses } from '../../utils/positionColors';
import { padDistrictNumber } from '../../utils/districtDisplay';

export type CandidateTableColumn =
  | 'checkbox'
  | 'name'
  | 'workspaces'
  | 'office'
  | 'district'
  | 'party'
  | 'year'
  | 'status'
  | 'incumbent'
  | 'funds'
  | 'contact'
  | 'web'
  | 'staff'
  | 'position'
  | 'notes'
  | 'custom_fields'
  | 'actions';

interface CandidateTableProps {
  candidates: BaseCandidate[];
  context: CandidateRenderContext;
  columns?: CandidateTableColumn[];
  sort?: SortState;
  onSort?: (field: CandidateSortField) => void;
  mode?: 'directory' | 'workspace';
  isLoading?: boolean;
  renderNotesCell?: (candidate: BaseCandidate) => React.ReactNode;
  onRowClick?: (candidate: BaseCandidate) => void;
  /** Org reports have extra columns; enable horizontal scroll so Web/Social is not clipped */
  horizontalScroll?: boolean;
  /** Report surfaces pass 'report' for the grey header identity. */
  variant?: 'default' | 'report';
  /**
   * Organization custom fields to render as columns, one per field, inserted
   * before Actions. Callers derive these from the data so only fields holding
   * values take up space.
   */
  customFieldColumns?: CandidateCustomFieldColumn[];
}

const SOCIAL_LABEL: Record<string, string> = {
  x: 'X', instagram: 'IG', facebook: 'FB', linkedin: 'LI', bluesky: 'BS', youtube: 'YT',
};

/** Compact amount without a currency mark — the cell's DollarSign glyph is the mark. */
function formatCompactCurrency(amount: number): string {
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `${(amount / 1_000).toFixed(1)}K`;
  return amount.toLocaleString();
}

const DEFAULT_DIRECTORY_COLUMNS: CandidateTableColumn[] = [
  'name', 'office', 'district', 'party', 'year', 'status', 'incumbent', 'funds', 'contact', 'web', 'actions',
];

const DEFAULT_WORKSPACE_COLUMNS: CandidateTableColumn[] = [
  'name', 'office', 'district', 'party', 'status', 'incumbent', 'funds', 'contact', 'position', 'notes', 'custom_fields', 'actions',
];

const CandidateTable: React.FC<CandidateTableProps> = ({
  candidates,
  context,
  columns,
  sort,
  onSort,
  mode = 'directory',
  isLoading = false,
  renderNotesCell,
  onRowClick,
  horizontalScroll = false,
  variant = 'default',
  customFieldColumns = [],
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const candidateBase = context.isPublicView ? '/public/candidates' : '/candidates';
  const legislatorBase = context.isPublicView ? '/public/legislators' : '/legislators';

  const activeColumns = columns || (mode === 'workspace' ? DEFAULT_WORKSPACE_COLUMNS : DEFAULT_DIRECTORY_COLUMNS);

  const handleRowClick = (candidate: BaseCandidate) => {
    if (onRowClick) {
      onRowClick(candidate);
    } else if (context.onNavigate) {
      context.onNavigate(candidate.id);
    } else {
      navigate(`${candidateBase}/${candidate.id}`);
    }
  };

  const renderHeader = (column: CandidateTableColumn) => {
    const sortDir = sort?.direction || 'asc';

    switch (column) {
      case 'checkbox':
        return (
          <StandardTableHeaderCell key="checkbox" isCheckbox>
            <input
              type="checkbox"
              checked={context.selectedCandidateIds?.size === candidates.length && candidates.length > 0}
              onChange={() => {
                if (context.selectedCandidateIds?.size === candidates.length) {
                  candidates.forEach(c => context.onSelectCandidate?.(c.id, false));
                } else {
                  candidates.forEach(c => context.onSelectCandidate?.(c.id, true));
                }
              }}
              className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
            />
          </StandardTableHeaderCell>
        );
      case 'name':
        return (
          <StandardTableHeaderCell key="name" sortable sortKey="name" currentSortKey={sort?.field} sortDirection={sortDir} onSort={onSort as (k: string) => void} columnType="nowrap">
            Name
          </StandardTableHeaderCell>
        );
      case 'workspaces':
        return (
          <StandardTableHeaderCell key="workspaces" columnType="expand">
            Workspaces
          </StandardTableHeaderCell>
        );
      case 'office':
        return (
          <StandardTableHeaderCell key="office" sortable sortKey="office_sought" currentSortKey={sort?.field} sortDirection={sortDir} onSort={onSort as (k: string) => void} columnType="nowrap">
            Office
          </StandardTableHeaderCell>
        );
      case 'district':
        return (
          <StandardTableHeaderCell key="district" sortable sortKey="district" currentSortKey={sort?.field} sortDirection={sortDir} onSort={onSort as (k: string) => void} columnType="nowrap" responsiveHide="md">
            District
          </StandardTableHeaderCell>
        );
      case 'party':
        return (
          <StandardTableHeaderCell key="party" sortable sortKey="party" currentSortKey={sort?.field} sortDirection={sortDir} onSort={onSort as (k: string) => void} columnType="narrow">
            Party
          </StandardTableHeaderCell>
        );
      case 'year':
        return (
          <StandardTableHeaderCell key="year" sortable sortKey="election_year" currentSortKey={sort?.field} sortDirection={sortDir} onSort={onSort as (k: string) => void} columnType="narrow" responsiveHide="md">
            Year
          </StandardTableHeaderCell>
        );
      case 'status':
        return (
          <StandardTableHeaderCell key="status" sortable sortKey="status" currentSortKey={sort?.field} sortDirection={sortDir} onSort={onSort as (k: string) => void} columnType="narrow">
            Status
          </StandardTableHeaderCell>
        );
      case 'incumbent':
        return (
          <StandardTableHeaderCell key="incumbent" columnType="nowrap" responsiveHide="md">
            Incumbent
          </StandardTableHeaderCell>
        );
      case 'funds':
        return (
          <StandardTableHeaderCell key="funds" sortable sortKey="total_funds_raised" currentSortKey={sort?.field} sortDirection={sortDir} onSort={onSort as (k: string) => void} columnType="nowrap" responsiveHide="md">
            Funds Raised
          </StandardTableHeaderCell>
        );
      case 'contact':
        return (
          <StandardTableHeaderCell key="contact" columnType="fit" responsiveHide="lg">
            Contact
          </StandardTableHeaderCell>
        );
      case 'web':
        return (
          <StandardTableHeaderCell key="web" columnType="nowrap" className="min-w-[11rem]">
            Web / Social
          </StandardTableHeaderCell>
        );
      case 'staff':
        return (
          <StandardTableHeaderCell key="staff" columnType="narrow" responsiveHide="lg">
            Staff
          </StandardTableHeaderCell>
        );
      case 'position':
        return (
          <StandardTableHeaderCell key="position" columnType="narrow">
            Position
          </StandardTableHeaderCell>
        );
      case 'notes':
        return (
          <StandardTableHeaderCell key="notes" columnType="narrow">
            Notes
          </StandardTableHeaderCell>
        );
      case 'custom_fields':
        return (
          <StandardTableHeaderCell key="custom_fields" columnType="narrow">
            Fields
          </StandardTableHeaderCell>
        );
      case 'actions':
        return (
          <StandardTableHeaderCell key="actions" columnType="actions">
            Actions
          </StandardTableHeaderCell>
        );
      default:
        return null;
    }
  };

  const renderCell = (column: CandidateTableColumn, candidate: BaseCandidate) => {
    const currentUrl = location.pathname + location.search;

    switch (column) {
      case 'checkbox':
        return (
          <StandardTableCell key="checkbox" isCheckbox>
            <input
              type="checkbox"
              checked={context.selectedCandidateIds?.has(candidate.id) || false}
              onChange={(e) => {
                e.stopPropagation();
                context.onSelectCandidate?.(candidate.id, e.target.checked);
              }}
              className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
            />
          </StandardTableCell>
        );

      case 'name': {
        const partyColor = getPartyTextColor(candidate.partyAffiliation || undefined);
        return (
          <StandardTableCell key="name">
            <div className="flex items-center gap-2 whitespace-nowrap">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`${candidateBase}/${candidate.id}`, { state: { returnUrl: currentUrl } });
                }}
                className={`font-medium hover:underline cursor-pointer ${partyColor}`}
              >
                {formatPersonName(candidate)}
              </button>
              {candidate.isPriority && (
                <Star className="w-4 h-4 text-yellow-500 fill-current flex-shrink-0" />
              )}
              {candidate.isIncumbent && (
                <Chip label="Inc." variant="info" size="xs" />
              )}
            </div>
          </StandardTableCell>
        );
      }

      case 'workspaces': {
        const workspaces = (candidate as any).workspaces as Array<{ id: string; name: string; position?: string }> | undefined;
        if (!workspaces || workspaces.length === 0) {
          return <StandardTableCell key="workspaces"><span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span></StandardTableCell>;
        }
        return (
          <StandardTableCell key="workspaces">
            <WorkspaceBadgeList workspaces={workspaces} />
          </StandardTableCell>
        );
      }

      case 'office':
        return (
          <StandardTableCell key="office">
            <span className="text-sm text-text-secondary dark:text-text-dark-secondary whitespace-nowrap">
              {candidate.officeSought || '—'}
            </span>
          </StandardTableCell>
        );

      case 'district':
        return (
          <StandardTableCell key="district" responsiveHide="md">
            <span className="text-sm text-text-secondary dark:text-text-dark-secondary whitespace-nowrap">
              {padDistrictNumber(candidate.district) || '—'}
            </span>
          </StandardTableCell>
        );

      case 'party': {
        const party = candidate.partyAffiliation || '';
        return (
          <StandardTableCell key="party">
            {party ? (
              <Chip label={formatPartyDisplay(party)} variant={getPartyChipVariant(party)} size="sm" />
            ) : (
              <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>
            )}
          </StandardTableCell>
        );
      }

      case 'year':
        return (
          <StandardTableCell key="year" responsiveHide="md">
            <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
              {candidate.electionYear}
            </span>
          </StandardTableCell>
        );

      case 'status':
        return (
          <StandardTableCell key="status">
            <Chip
              label={candidate.status?.charAt(0).toUpperCase() + candidate.status?.slice(1)}
              variant={statusVariant(candidate.status)}
              size="sm"
            />
          </StandardTableCell>
        );

      case 'incumbent': {
        const incName = candidate.seatIncumbentName;
        const incId = candidate.seatIncumbentId;
        return (
          <StandardTableCell key="incumbent" responsiveHide="md">
            {incName ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (incId) navigate(`${legislatorBase}/${incId}`);
                }}
                className={`text-sm font-medium whitespace-nowrap ${incId ? 'text-primary-600 dark:text-primary-400 hover:underline cursor-pointer' : 'text-text-secondary dark:text-text-dark-secondary'}`}
              >
                {incName}
              </button>
            ) : (
              <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>
            )}
          </StandardTableCell>
        );
      }

      case 'funds': {
        const amount = candidate.totalFundsRaised;
        const hasAmount = amount != null && amount > 0;
        return (
          <StandardTableCell key="funds" responsiveHide="md">
            {hasAmount ? (
              <span className="flex items-center gap-1 text-sm font-medium text-text-secondary dark:text-text-dark-secondary whitespace-nowrap">
                <DollarSign className="w-3.5 h-3.5 text-text-tertiary" />
                {formatCompactCurrency(amount)}
              </span>
            ) : (
              <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>
            )}
          </StandardTableCell>
        );
      }

      case 'contact':
        return (
          <StandardTableCell key="contact" responsiveHide="lg">
            <div className="flex flex-col gap-1">
              {candidate.email && (
                <a
                  href={`mailto:${candidate.email}`}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
                  title={candidate.email}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Mail className="w-3 h-3" />
                  <span className="truncate max-w-[150px]">{candidate.email}</span>
                </a>
              )}
              {candidate.phone && (
                <a
                  href={`tel:${candidate.phone}`}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
                  title={formatPhoneNumber(candidate.phone)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Phone className="w-3 h-3" />
                  <span>{formatPhoneNumber(candidate.phone)}</span>
                </a>
              )}
            </div>
          </StandardTableCell>
        );

      case 'web': {
        const hasSocial = candidate.socialMedia && candidate.socialMedia.length > 0;
        const hasWeb = !!candidate.website;
        if (!hasWeb && !hasSocial) {
          return (
            <StandardTableCell key="web" className="min-w-[11rem]">
              <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>
            </StandardTableCell>
          );
        }
        const webUrl = hasWeb
          ? (candidate.website!.startsWith('http') ? candidate.website! : `https://${candidate.website}`)
          : '';
        return (
          <StandardTableCell key="web" className="min-w-[11rem]">
            <div className="flex flex-col gap-1">
              {hasWeb && (
                <a
                  href={webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
                  title={candidate.website!}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Globe className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate max-w-[180px]">{candidate.website!.replace(/^https?:\/\//, '')}</span>
                </a>
              )}
              {hasSocial && (
                <div className="flex items-center gap-2">
                  {candidate.socialMedia!.map((sm: SocialMediaLink) => (
                    <a
                      key={sm.platform}
                      href={sm.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
                      title={`@${sm.handle}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Share2 className="w-3 h-3" />
                      <span>{SOCIAL_LABEL[sm.platform] || sm.platform}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </StandardTableCell>
        );
      }

      case 'staff':
        return (
          <StandardTableCell key="staff" responsiveHide="lg">
            {candidate.activeStaffCount != null && candidate.activeStaffCount > 0 ? (
              <span className="flex items-center gap-1 text-sm text-text-secondary dark:text-text-dark-secondary">
                <Users className="w-3.5 h-3.5" />
                {candidate.activeStaffCount}
              </span>
            ) : (
              <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>
            )}
          </StandardTableCell>
        );

      case 'position': {
        const position = candidate.position || 'Neutral';
        return (
          <StandardTableCell key="position">
            {/*
              canManage, not canEdit: a position is the organisation speaking, so only
              a workspace manager may set one (2026-08-18). A contributor still SEES it
              — they cannot work to a stance they cannot read — which is what the
              read-only branch below already did for viewers and report mode.
            */}
            {context.permissions?.canManage ? (
              <select
                value={position}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  e.stopPropagation();
                  context.onPositionChange?.(candidate.id, e.target.value);
                }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                className={`text-xs px-2 py-1 border border-border-light dark:border-border-dark rounded-md font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${getPositionColorClasses(position)}`}
              >
                <option value="Support">Support</option>
                <option value="Oppose">Oppose</option>
                <option value="Monitor">Monitor</option>
                <option value="Neutral">Neutral</option>
              </select>
            ) : (
              <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getPositionColorClasses(position)}`}>
                {position}
              </span>
            )}
          </StandardTableCell>
        );
      }

      case 'notes':
        return (
          <StandardTableCell key="notes">
            {renderNotesCell ? renderNotesCell(candidate) : (
              <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>
            )}
          </StandardTableCell>
        );

      case 'custom_fields':
        return (
          <StandardTableCell key="custom_fields">
            {context.workspaceId ? (
              <div onClick={(e) => e.stopPropagation()}>
                <CustomFieldBadge
                  entityType="candidate"
                  entityId={candidate.id}
                  entityDisplayName={candidate.fullName}
                  workspaceId={context.workspaceId}
                  count={candidate.customFieldCount}
                  canEdit={context.permissions?.canEdit}
                />
              </div>
            ) : (
              <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>
            )}
          </StandardTableCell>
        );

      case 'actions': {
        const isTracked = toTrackedCandidateIdSet(context.trackedCandidateIds).has(candidate.id);
        return (
          <StandardTableCell key="actions">
            <div className="flex items-center gap-2">
              <button
                className="p-1 text-text-tertiary hover:text-text-secondary dark:text-text-dark-tertiary dark:hover:text-text-dark-secondary transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`${candidateBase}/${candidate.id}`, { state: { returnUrl: currentUrl } });
                }}
                title="View details"
              >
                <Eye className="w-4 h-4" />
              </button>

              {context.onBookmark && (
                <button
                  className={`p-1 transition-colors ${
                    isTracked
                      ? 'text-blue-600 hover:text-blue-700'
                      : 'text-text-tertiary hover:text-blue-600 dark:text-text-dark-tertiary'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    context.onBookmark!(candidate.id, candidate.fullName);
                  }}
                  title={isTracked ? 'Manage tracking' : 'Track candidate'}
                >
                  <Bookmark className="w-4 h-4" fill={isTracked ? 'currentColor' : 'none'} />
                </button>
              )}

              {context.onPriorityToggle && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    context.onPriorityToggle!(candidate.id, !candidate.isPriority);
                  }}
                  className={`p-1 transition-colors ${
                    candidate.isPriority
                      ? 'text-yellow-500 hover:text-yellow-600'
                      : 'text-text-tertiary dark:text-text-dark-tertiary hover:text-yellow-500'
                  }`}
                  title={candidate.isPriority ? 'Remove from priority' : 'Mark as priority'}
                >
                  <Star className="w-4 h-4" fill={candidate.isPriority ? 'currentColor' : 'none'} />
                </button>
              )}

              {context.onRemove && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    context.onRemove!(candidate);
                  }}
                  className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-error-600 transition-colors"
                  title="Stop tracking"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </StandardTableCell>
        );
      }

      default:
        return null;
    }
  };

  const renderCustomFieldHeader = (col: CandidateCustomFieldColumn) => (
    <StandardTableHeaderCell key={`cf-${col.definitionId}`} columnType="nowrap">
      {col.name}
    </StandardTableHeaderCell>
  );

  const renderCustomFieldCell = (col: CandidateCustomFieldColumn, candidate: BaseCandidate) => {
    const displayValues = getCustomFieldDisplayValues(candidate, col.definitionId);

    return (
      <StandardTableCell key={`cf-${col.definitionId}`}>
        {displayValues.length === 0 ? (
          <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>
        ) : (
          <div className="space-y-0.5 max-w-[16rem]">
            {displayValues.map((display, index) => (
              <div key={index} className="text-sm text-text-primary dark:text-text-dark-primary break-words">
                {display.text}
                {display.workspaceName && (
                  <span className="ml-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    ({display.workspaceName})
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </StandardTableCell>
    );
  };

  // Custom field columns sit before Actions so the action buttons stay last.
  const actionsIndex = activeColumns.indexOf('actions');
  const insertAt = actionsIndex === -1 ? activeColumns.length : actionsIndex;

  const withCustomFields = (fixed: React.ReactNode[], custom: React.ReactNode[]) =>
    custom.length === 0 ? fixed : [...fixed.slice(0, insertAt), ...custom, ...fixed.slice(insertAt)];

  return (
    <StandardTable variant={variant} loading={isLoading} horizontalScroll={horizontalScroll || customFieldColumns.length > 0}>
      <StandardTableHeader>
        <tr>
          {withCustomFields(
            activeColumns.map(col => renderHeader(col)),
            customFieldColumns.map(col => renderCustomFieldHeader(col))
          )}
        </tr>
      </StandardTableHeader>
      <StandardTableBody>
        {candidates.map((candidate) => (
          <StandardTableRow
            key={candidate.id}
            hoverable
            onClick={() => handleRowClick(candidate)}
          >
            {withCustomFields(
              activeColumns.map(col => renderCell(col, candidate)),
              customFieldColumns.map(col => renderCustomFieldCell(col, candidate))
            )}
          </StandardTableRow>
        ))}
      </StandardTableBody>
    </StandardTable>
  );
};

export default CandidateTable;
